import articleImage1 from '../../images/articles/article-image-1.png';
import articleImage2 from '../../images/articles/article-image-2.png';
import articleImage3 from '../../images/articles/article-image-3.png';
import articleImage4 from '../../images/articles/article-image-4.png';

const articles = [
  {
    slug: 'adorcv-ai-recruitment-automation-overview',
    category: 'Product Overview',
    title: 'AdorCV: The AI Application That Automates HR and Recruitment Tasks',
    excerpt:
      'An overview of how AdorCV helps HR teams standardize, manage, and match candidate CVs',
    date: '2026-06-10',
    coverImage: articleImage1,
    coverImageAlt: 'Two recruiters shaking hands with a candidate across a desk with a laptop and documents',
    content: `HR and recruitment teams handle a large amount of administrative work every day. Candidate CVs arrive in different formats, need to be reviewed, organized, stored, and eventually matched against open roles. Each of these steps takes time, and when done manually across many candidates and many open positions, that time adds up quickly. AdorCV is an AI application built specifically to automate and streamline these key HR and recruitment tasks within teams, reducing manual work and improving consistency across the entire hiring process.

This article gives an overview of what AdorCV does, the features it includes, and the benefits it offers to HR teams looking to bring more structure and efficiency to how they manage candidate data.

### Why HR Teams Need This Kind of Automation

Recruitment involves a large volume of repetitive administrative work: reviewing CVs, organizing them, keeping profiles up to date, and comparing candidates against role requirements. When this work is done manually, it takes time away from the parts of recruitment that require human judgment, such as evaluating fit and communicating with candidates. AdorCV is built to automate and streamline exactly these key HR and recruitment tasks, so that formatting, organizing, and searching candidate data no longer require constant manual effort.

### What AdorCV Does

AdorCV allows users to upload candidate CVs in various formats and automatically converts them into a unified, company-branded structure. This ensures consistency across all documents, regardless of how each CV originally arrived — whether as a Word document, a PDF, or another common file type.

Once a CV has been processed, it can be exported in the format that best fits the situation: PDF, editable DOCX, or PPT. This gives HR teams flexibility depending on whether a document needs to be shared internally, sent externally, or presented.

### Key Features of AdorCV

#### Automated CV Standardization

Candidate CVs uploaded in various formats are automatically converted into a unified, company-branded structure. This removes the need to manually reformat each document and ensures every candidate profile looks consistent, no matter how differently each candidate originally submitted their CV.

#### Flexible Export Options

Once standardized, CVs can be exported as PDF, editable DOCX, or PPT files, depending on the format required for a given step in the recruitment process. This means the same candidate profile can be adapted to different needs without additional manual formatting.

#### User-Based Login and Role-Specific Permissions

AdorCV includes user-based login with role-specific permissions, allowing organizations to control who can access, edit, or manage candidate data within the platform. Each user works from their own account, and their level of access can be set according to their role.

#### Searchable CV Database with Advanced Filters

All candidate CVs are stored in a searchable database with advanced filters, making it easier to locate specific profiles based on relevant criteria rather than manually browsing through every stored CV.

#### Tools to Edit or Enrich Candidate Profiles

Candidate profiles are not static once uploaded. AdorCV includes tools that allow HR teams to edit or enrich profiles, keeping candidate information accurate and up to date as circumstances change.

#### AI Matching Between Job Descriptions and CVs

A dedicated section for Job Descriptions enables AI matching of required skills between job descriptions and existing CVs in the database. This helps HR teams identify relevant candidates for a role based on the skills already documented in their CV database, without reviewing every profile manually.

#### Fully Customizable Templates

Templates used for standardized CVs are fully customizable, allowing companies to align exported documents with their own branding and formatting preferences rather than relying on a fixed, generic layout.

#### Multilingual Support

AdorCV supports documents in both English and French, allowing HR teams to generate and manage candidate profiles in either language within the same system.

### How These Features Work Together

Each feature in AdorCV supports the others. Standardized CVs feed into a consistent, searchable database. That database, in turn, is what makes AI matching against job descriptions possible, since the AI is comparing structured, consistent candidate data rather than a mix of unrelated formats. Role-specific permissions ensure that as more people within an HR team use the database, access remains controlled and appropriate. And because templates are customizable and available in English and French, this entire workflow can be adapted to a company's branding and language requirements.

### Benefits for HR Teams

By combining these features, AdorCV helps HR teams save time and improve efficiency across recruitment workflows.

- **Less manual formatting work**, since CVs are automatically standardized into a unified structure
- **Easier collaboration**, supported by role-specific permissions and a centralized database
- **Faster access to relevant candidates**, through advanced search filters and AI-powered matching
- **Consistent, branded documentation**, thanks to fully customizable templates
- **Support for bilingual operations**, with documents available in English and French

### Frequently Asked Questions

#### What file formats can AdorCV export CVs to?

AdorCV can export standardized candidate profiles as PDF, editable DOCX, or PPT files.

#### Does AdorCV support languages other than English?

Yes, AdorCV supports documents in both English and French.

#### How does AdorCV match candidates to job descriptions?

AdorCV includes a dedicated Job Descriptions section where AI matches the required skills listed in a job description against the skills found in existing CVs in the database.

#### Can candidate data access be restricted within AdorCV?

Yes, AdorCV features user-based login with role-specific permissions, allowing control over who can access or manage candidate data.

#### Can candidate profiles be changed after a CV has been standardized?

Yes, AdorCV includes tools to edit or enrich candidate profiles at any time after they have been added to the database.

### Who AdorCV Is Built For

AdorCV is designed for HR professionals and recruiters who regularly work with candidate CVs — reviewing new applications, maintaining a database of past candidates, and comparing candidates against open roles. Because the application automates and streamlines HR and recruitment tasks within teams, it is built to be used collaboratively, with multiple team members accessing the same standardized database under their own permissions.

### Final Thoughts

AdorCV brings together CV standardization, secure access control, a searchable candidate database, AI-powered job matching, and multilingual support in a single application. For HR teams looking to reduce manual administrative work and bring more consistency to their recruitment process, AdorCV offers a practical, AI-driven way to manage candidate CVs from upload to export.`,
  },
  {
    slug: 'cv-standardization-and-export-with-adorcv',
    category: 'CV Management',
    title: 'CV Standardization and Export with AdorCV: A Unified, Branded Format for Every Candidate',
    excerpt: 'How AdorCV converts CVs uploaded in various formats into consistent, exportable documents',
    date: '2026-06-17',
    coverImage: articleImage2,
    coverImageAlt:
      'A man reviewing hiring analytics and charts on his phone at a desk with a monitor displaying dashboards in the background',
    content: `One of the core functions of AdorCV, the AI application built to automate and streamline HR and recruitment tasks, is CV standardization. This article looks specifically at how AdorCV handles CV uploads, formatting, and exports — one of the features designed to help HR teams save time and improve efficiency across recruitment workflows.

### Uploading CVs in Various Formats

AdorCV allows users to upload candidate CVs in various formats. Instead of requiring HR teams to convert or clean up documents before adding them to the system, the application is built to accept CVs as they are received. This is one of the first ways AdorCV automates and streamlines HR and recruitment tasks: removing the manual step of preparing a document before it can be added to a company's system.

### Automatic Conversion into a Unified, Company-Branded Structure

Once uploaded, AdorCV automatically converts each CV into a unified, company-branded structure. This ensures consistency across all documents, regardless of how differently each candidate's original CV was formatted. Instead of a mix of layouts, fonts, and structures, HR teams end up with a consistent set of candidate profiles that follow the same branded template.

This consistency matters throughout the recruitment process. When every candidate profile follows the same structure, it becomes easier to review, compare, and store CVs without adjusting to a new layout each time.

### Fully Customizable Templates

The templates used for this standardization process are fully customizable. This means the unified structure applied to every CV can be adapted to match a company's specific branding and formatting requirements, rather than relying on a fixed, generic layout. As branding needs change, the templates used across AdorCV can be updated accordingly.

### Editing and Enriching Candidate Profiles

Standardization doesn't lock a profile in place. AdorCV includes tools to edit or enrich candidate profiles, so HR teams can update information, add details, or make corrections to a candidate's profile after it has been standardized. This keeps candidate documentation accurate even as new information becomes available over time.

### Exporting CVs: PDF, DOCX, or PPT

After a CV has been standardized, it can be exported as a PDF, an editable DOCX file, or a PPT file. This flexibility allows HR teams to choose the export format that best matches how the document will be used or shared.

#### PDF Export

A PDF export is useful when a finalized, non-editable version of a candidate profile is needed, for example when a document needs to be shared in a fixed, presentation-ready format.

#### Editable DOCX Export

An editable DOCX export allows the candidate profile to be further adjusted or customized after export, giving HR teams the flexibility to make additional changes outside of AdorCV if needed.

#### PPT Export

A PPT export presents the candidate profile in a presentation-style format, which can be useful when candidate information needs to be shared in a slide-based format.

### Multilingual Templates

AdorCV supports documents in both English and French. This means the standardized, customizable templates used for CV formatting are available for both languages, allowing HR teams to generate consistent candidate documentation regardless of which language is required for a given document or audience.

### How Standardization Connects to the Rest of AdorCV

CV standardization is closely connected to other features within AdorCV. Once a CV has been converted into AdorCV's unified structure, it becomes part of the searchable CV database with advanced filters, and it can also be included in AI matching against job descriptions in the dedicated Job Descriptions section. Standardized, structured data is what allows these other features to function effectively, since consistent formatting makes it easier for the system to organize and compare candidate information.

User-based login and role-specific permissions also apply to standardized CVs stored within AdorCV, meaning access to this structured candidate data remains controlled according to each user's role within the HR team.

### Benefits of Automated CV Standardization

- **Consistency across all documents**, since every CV follows the same company-branded structure
- **Time savings**, by removing the need to manually reformat each CV
- **Flexibility**, through fully customizable templates and multiple export formats
- **Up-to-date profiles**, thanks to tools for editing and enriching candidate information
- **Bilingual documentation**, with support for both English and French

### Frequently Asked Questions

#### What happens to a CV after it's uploaded to AdorCV?

AdorCV automatically converts the uploaded CV into a unified, company-branded structure, regardless of its original format.

#### Can the CV template be customized?

Yes, templates used for standardization in AdorCV are fully customizable.

#### What export formats are available for standardized CVs?

Standardized CVs can be exported as PDF, editable DOCX, or PPT files.

#### Can a candidate profile be edited after standardization?

Yes, AdorCV includes tools to edit or enrich candidate profiles at any time.

#### Is CV standardization available in languages other than English?

Yes, AdorCV supports documents in both English and French.

#### Who can access a standardized CV once it's stored in AdorCV?

Access is controlled through user-based login and role-specific permissions, so only users with appropriate access can view or manage a given candidate profile.

### Why Consistent Formatting Matters

When candidate CVs arrive in a wide range of formats, reviewing and comparing them becomes harder than it needs to be. A unified, company-branded structure means every candidate profile can be reviewed the same way, without HR teams needing to adjust to a new layout each time. This is part of why CV standardization sits at the center of what AdorCV automates and streamlines within HR and recruitment workflows.

### Final Thoughts

CV standardization is one of the foundational features of AdorCV. By automatically converting CVs uploaded in various formats into a unified, company-branded structure, and offering flexible export options in PDF, DOCX, or PPT, AdorCV helps HR teams maintain consistent, professional candidate documentation while saving time on manual formatting work.`,
  },
  {
    slug: 'ai-matching-job-descriptions-and-cvs',
    category: 'AI Matching',
    title: 'AI Matching in AdorCV: Connecting Job Descriptions with Existing CVs',
    excerpt: "How AdorCV's dedicated Job Descriptions section uses AI to match required skills to candidates",
    date: '2026-06-24',
    coverImage: articleImage3,
    coverImageAlt: 'Two people reviewing printed CVs with candidate photos spread across a desk alongside a tablet',
    content: `Beyond CV standardization and database management, AdorCV includes a dedicated section for Job Descriptions that enables AI matching of required skills between job descriptions and existing CVs. This article focuses on this specific feature and how it fits into AdorCV's broader goal of helping HR teams save time and improve efficiency across recruitment workflows.

### What the Job Descriptions Section Does

Within AdorCV, users have access to a dedicated section for Job Descriptions. This section is built specifically to compare the requirements of a role against the candidate CVs already stored in the AdorCV database. Rather than treating job descriptions and candidate CVs as separate, disconnected documents, AdorCV brings them together within the same application.

### How AI Matching Works

AdorCV's AI matching identifies the required skills listed in a job description and compares them against the skills present in existing CVs within the database. This allows HR teams to see which candidates already in their database align with the requirements of a given role, without needing to manually review each CV individually.

Because this matching is based on required skills, it gives HR teams a structured starting point when evaluating which candidates might be relevant to a new or existing job description.

### Why This Matters for HR Teams

Matching job requirements to candidate skills is a core part of recruitment, and doing it manually across a CV database takes time. By automating this comparison, AdorCV's Job Descriptions section supports one of the application's central goals: helping HR teams save time and improve efficiency across recruitment workflows.

This is especially relevant as a candidate database grows. The more CVs an HR team has stored, the more time it would normally take to manually compare each one against a new job description. AI matching removes this manual step, allowing HR teams to focus their time on reviewing the candidates that have already been identified as relevant.

### How AI Matching Connects to Other AdorCV Features

#### Built on Standardized CV Data

AI matching works with the CVs stored in AdorCV's database — the same CVs that were automatically converted into a unified, company-branded structure when uploaded. Because this data follows a consistent structure, it can be compared against job description requirements more effectively than a mix of differently formatted documents.

#### Supported by the Searchable CV Database

AdorCV's searchable CV database with advanced filters works alongside the Job Descriptions section, giving HR teams more than one way to identify relevant candidates — either through AI matching against a specific job description or through direct filtering and search.

#### Editable Profiles Keep Matching Relevant

Since AdorCV includes tools to edit or enrich candidate profiles, the information used in AI matching can be kept up to date, helping ensure that skill comparisons reflect a candidate's current profile rather than outdated information.

#### Controlled Access Through Permissions

As with other parts of AdorCV, access to the Job Descriptions section and the matching results it produces is governed by user-based login and role-specific permissions, so that only appropriate team members can view or act on this information.

#### Available in English and French

As with the rest of AdorCV, the Job Descriptions section supports documents in both English and French, in line with the application's overall multilingual support.

### Benefits of AI-Powered Job and CV Matching

- **Faster identification of relevant candidates**, without manually reviewing every CV in the database
- **Better use of an existing CV database**, by systematically comparing stored candidate skills against new job requirements
- **Consistent matching criteria**, based on the required skills defined in each job description
- **Time savings for HR teams**, supporting more efficient recruitment workflows overall
- **A structured starting point for evaluation**, since matched candidates can then be reviewed further using the searchable database and advanced filters

### Frequently Asked Questions

#### What does the Job Descriptions section in AdorCV do?

It enables AI matching of required skills between a job description and the CVs already stored in AdorCV's database.

#### What does AdorCV compare when matching candidates to a role?

AdorCV compares the required skills listed in a job description against the skills present in existing candidate CVs.

#### Does AI matching work with CVs stored anywhere, or only within AdorCV?

AI matching in AdorCV works with the CVs stored in its own searchable database, which have been standardized into the platform's unified structure.

#### Is the Job Descriptions section available in French as well as English?

Yes, AdorCV supports documents in both English and French across the application, including the Job Descriptions section.

#### Can HR teams still search manually after using AI matching?

Yes, the Job Descriptions section works alongside AdorCV's searchable CV database with advanced filters, so HR teams can refine or supplement AI matching results with manual search.

### AI Matching as Part of a Broader Recruitment Workflow

AI matching in AdorCV is not a standalone tool — it is one part of an application built to automate and streamline HR and recruitment tasks as a whole. The same database used for matching is also the one HR teams search directly, edit and enrich over time, and export candidate profiles from. Viewed this way, the Job Descriptions section is simply another way of getting value out of the same structured candidate data that powers the rest of AdorCV.

### Final Thoughts

The dedicated Job Descriptions section is one of the features that sets AdorCV apart as more than just a CV formatting tool. By enabling AI matching of required skills between job descriptions and existing CVs, AdorCV helps HR teams make better use of their candidate database and move more efficiently from job requirements to relevant candidates.`,
  },
  {
    slug: 'managing-candidate-data-search-security-permissions',
    category: 'Data & Security',
    title: 'Managing Candidate Data in AdorCV: Search, Security, and Permissions',
    excerpt: "A look at AdorCV's searchable CV database, advanced filters, and role-specific access controls",
    date: '2026-07-01',
    coverImage: articleImage4,
    coverImageAlt: 'A man sitting at a desk checking his phone with a notebook and open-plan office in the background',
    content: `Storing and organizing candidate CVs is only useful if HR teams can find, manage, and protect that information effectively. AdorCV, the AI application built to automate and streamline HR and recruitment tasks, includes a searchable CV database with advanced filters, along with user-based login and role-specific permissions, to support exactly this need.

### A Searchable CV Database with Advanced Filters

Every CV processed through AdorCV becomes part of a searchable CV database. Advanced filters allow HR teams to narrow down candidate profiles based on relevant criteria, making it easier to locate specific CVs without manually browsing through the entire database. As the number of candidate profiles grows, this search capability becomes increasingly important for keeping the database usable and efficient.

### Editing and Enriching Candidate Profiles

AdorCV includes tools to edit or enrich candidate profiles directly within the database. This means candidate information doesn't remain static after upload — HR teams can update profiles as new information becomes available, keeping the database accurate over time rather than letting profiles become outdated.

### User-Based Login and Role-Specific Permissions

Access to AdorCV is managed through user-based login, meaning each person using the platform has their own individual account. On top of this, AdorCV features role-specific permissions, allowing different levels of access to be assigned depending on a user's role within the HR team.

This structure allows organizations to control who can view, edit, or manage candidate data within the platform, supporting more controlled and organized access to sensitive recruitment information. As more people within an HR team use AdorCV, this kind of access control becomes an important part of keeping the database well managed.

### How Database Management Connects to Other AdorCV Features

#### Structured Data from CV Standardization

The CVs available for search and filtering in AdorCV's database are the same CVs that were automatically converted into a unified, company-branded structure upon upload. This consistent structure is part of what makes advanced filtering effective, since every profile follows the same underlying format.

#### Supporting AI Matching

The searchable database also supports the dedicated Job Descriptions section, where AI matching compares required skills from job descriptions against the CVs stored in the database. A well-organized, searchable database is part of what makes this matching process useful in practice, since AI matching draws directly from the same structured candidate data.

#### Multilingual Documentation

AdorCV supports documents in both English and French, meaning the candidate database can include standardized profiles in either language, managed within the same searchable system rather than requiring separate databases per language.

#### Flexible Exports from the Database

Once a relevant candidate is found through search, filters, or AI matching, their profile can be exported directly as PDF, editable DOCX, or PPT, depending on how it needs to be used. This means the database is not just a storage location, but a starting point for producing ready-to-use candidate documentation.

### Why Search, Security, and Structure Matter Together

A candidate database only remains useful if it stays organized and accessible as it grows. Search and filtering make it possible to find relevant candidates quickly. Editing and enrichment tools keep the information in that database accurate. And user-based login with role-specific permissions ensures that as access to the database expands across an HR team, it does so in a controlled way. Together, these features are what allow AdorCV's database to remain both usable and appropriately managed over time.

### Benefits of AdorCV's Database and Access Controls

- **Faster candidate retrieval**, through advanced filters within the searchable CV database
- **Up-to-date candidate information**, supported by tools to edit and enrich profiles
- **Controlled access to sensitive data**, through user-based login and role-specific permissions
- **A consistent, structured database**, built on standardized CV data
- **Multilingual database management**, with support for English and French documentation

### Frequently Asked Questions

#### How can HR teams find specific candidates in AdorCV?

HR teams can use AdorCV's searchable CV database with advanced filters to locate candidate profiles based on relevant criteria.

#### Can candidate profiles be updated after they are added to the database?

Yes, AdorCV includes tools to edit or enrich candidate profiles at any time.

#### How does AdorCV control who can access candidate data?

AdorCV features user-based login with role-specific permissions, allowing different levels of access depending on a user's role.

#### Does the candidate database support multiple languages?

Yes, AdorCV supports documents in both English and French, and this extends to the candidate database as a whole.

#### Can candidates found through search or filters be exported directly?

Yes, once a candidate profile is located through the database, it can be exported as PDF, editable DOCX, or PPT.

### Managing a Growing Candidate Database Over Time

As an HR team continues to use AdorCV, the candidate database naturally grows, with more standardized CVs added over time. Advanced filters and role-specific permissions become more valuable as this happens, since they allow the database to stay searchable and appropriately controlled even as the number of candidate profiles and the number of team members accessing them increases.

### Final Thoughts

A recruitment tool is only as useful as its ability to keep candidate data organized, accessible, and appropriately protected. AdorCV combines a searchable CV database with advanced filters, tools to edit and enrich profiles, and user-based login with role-specific permissions, giving HR teams a structured and controlled way to manage candidate data as part of their broader recruitment workflow.`,
  },
];

export default articles;
